import * as THREE from "three";
import type { CritterKind } from "@dogdell/shared";
import { paintToonHuman, paintToonWalk, toonCharactersReady } from "./ToonCharacterAssets";

export interface SpriteSheet {
  texture: THREE.Texture;
  /** World-space plane size. */
  width: number;
  height: number;
  /** Feet→seat lift for furniture (world units). */
  seatY?: number;
  /**
   * For seated humans: fraction up from the sprite bottom where hips meet the seat.
   * Legs below this hang in front of the bench.
   */
  seatAnchor?: number;
  /**
   * Mesh Y = bottomY + height * footY. Default SPRITE_FOOT_Y (0.368) assumes
   * padded art; use ~0.5 when feet sit at the texture bottom.
   */
  footY?: number;
}

export type FurnitureKind =
  | "bench"
  | "hotdogStand"
  | "iceCreamCart"
  | "pretzelCart"
  | "balloonCart"
  | "popcornCart";

const critterCache = new Map<CritterKind, SpriteSheet>();
const humanCache = new Map<string, SpriteSheet>();
const furnitureCache = new Map<FurnitureKind, SpriteSheet>();

export function isFurnitureSprite(kind: string): kind is FurnitureKind {
  // Empty park benches stay Kenney 3D; carts stay 3D. Canvas benches are for NPC sitters only
  // (spawned via addFurnitureSprite, not this gate).
  void kind;
  return false;
}

/** Ambient park wildlife - side-view canvas sprites (RS-classic living things). */
export function critterSprite(kind: CritterKind): SpriteSheet {
  let sheet = critterCache.get(kind);
  if (!sheet) {
    const canvas = paintCritter(kind);
    sheet = {
      texture: canvasTex(canvas),
      width:
        kind === "pigeon" ? 0.78
        : kind === "duck" ? 1.12
        : kind === "duckling" ? 0.62
        : kind === "rabbit" ? 0.82
        : 0.86,
      height:
        kind === "rabbit" ? 0.92
        : kind === "pigeon" ? 0.62
        : kind === "duck" ? 0.82
        : kind === "duckling" ? 0.48
        : 0.78,
    };
    critterCache.set(kind, sheet);
  }
  return sheet;
}

export type HumanPose =
  | "sit"
  | "stand"
  | "cook"
  | "eat"
  | "chat"
  | "tennisReady"
  | "tennisSwing"
  | "basketReady"
  | "basketShoot"
  | "drumReady"
  | "drumHit"
  | "frisbeeReady"
  | "frisbeeThrow"
  | "frisbeeCatch"
  | "fishReady"
  | "fishCast";

type HairStyle = "short" | "side" | "puff" | "long" | "bun" | "scarf" | "crop" | "bald" | "braids" | "pony";

interface HumanLook {
  skin: string;
  hair: string;
  style: HairStyle;
  /** Soft cheek tint - muted on deeper complexions. */
  blush: string;
}

/**
 * Coherent looks spanning many ethnicities / hair styles.
 * Variant indexes into this list so park NPCs stay diverse.
 */
const HUMAN_LOOKS: readonly HumanLook[] = [
  // Fair / Northern European
  { skin: "#ffe0b2", hair: "#3e2723", style: "short", blush: "rgba(255,128,171,0.55)" },
  { skin: "#ffe8cc", hair: "#f9a825", style: "long", blush: "rgba(255,140,170,0.5)" },
  { skin: "#f5d0b0", hair: "#bf360c", style: "side", blush: "rgba(255,130,160,0.5)" },
  { skin: "#ffe0c2", hair: "#5d4037", style: "pony", blush: "rgba(255,128,171,0.5)" },
  // Light olive / Mediterranean / Southern European
  { skin: "#e8c4a0", hair: "#1b1b1b", style: "short", blush: "rgba(230,120,140,0.4)" },
  { skin: "#e0b892", hair: "#3e2723", style: "bun", blush: "rgba(220,110,130,0.4)" },
  { skin: "#dcc09a", hair: "#212121", style: "long", blush: "rgba(225,115,135,0.38)" },
  // East Asian
  { skin: "#f3d5b5", hair: "#111111", style: "crop", blush: "rgba(255,140,160,0.42)" },
  { skin: "#edd0a8", hair: "#0d0d0d", style: "long", blush: "rgba(250,135,155,0.4)" },
  { skin: "#f0d2b0", hair: "#1a1a1a", style: "bun", blush: "rgba(255,138,158,0.4)" },
  { skin: "#e8c9a8", hair: "#151515", style: "short", blush: "rgba(245,130,150,0.38)" },
  // Southeast Asian
  { skin: "#d4a574", hair: "#1a120b", style: "long", blush: "rgba(220,110,130,0.35)" },
  { skin: "#c9956a", hair: "#0f0f0f", style: "pony", blush: "rgba(210,100,120,0.35)" },
  // South Asian
  { skin: "#c68642", hair: "#1a1a1a", style: "long", blush: "rgba(200,95,110,0.32)" },
  { skin: "#d4a574", hair: "#212121", style: "scarf", blush: "rgba(210,100,120,0.32)" },
  { skin: "#b87333", hair: "#0d0d0d", style: "short", blush: "rgba(190,90,105,0.3)" },
  { skin: "#a9622e", hair: "#111111", style: "bun", blush: "rgba(185,88,100,0.3)" },
  // Middle Eastern / North African
  { skin: "#d2a679", hair: "#1c1c1c", style: "short", blush: "rgba(210,105,120,0.34)" },
  { skin: "#c4a484", hair: "#111111", style: "scarf", blush: "rgba(200,100,115,0.32)" },
  { skin: "#b88962", hair: "#0a0a0a", style: "crop", blush: "rgba(195,95,110,0.3)" },
  // Latino / mestizo / Indigenous American
  { skin: "#c6865a", hair: "#2c1810", style: "long", blush: "rgba(205,100,115,0.34)" },
  { skin: "#d9a066", hair: "#1a120b", style: "side", blush: "rgba(215,105,120,0.34)" },
  { skin: "#b56f45", hair: "#0f0f0f", style: "short", blush: "rgba(195,95,110,0.3)" },
  { skin: "#a67c52", hair: "#1a120b", style: "braids", blush: "rgba(190,92,108,0.3)" },
  { skin: "#8b5e3c", hair: "#0f0a05", style: "long", blush: "rgba(180,85,100,0.28)" },
  // Black / African diaspora
  { skin: "#8d5524", hair: "#0a0a0a", style: "puff", blush: "rgba(180,80,100,0.28)" },
  { skin: "#6b3f24", hair: "#111111", style: "braids", blush: "rgba(160,70,90,0.26)" },
  { skin: "#a06b3f", hair: "#1a1a1a", style: "crop", blush: "rgba(185,85,100,0.28)" },
  { skin: "#5c3317", hair: "#0d0d0d", style: "short", blush: "rgba(150,65,85,0.24)" },
  { skin: "#4a2c0a", hair: "#1a1a1a", style: "bald", blush: "rgba(140,60,80,0.22)" },
  { skin: "#7a4a28", hair: "#0a0a0a", style: "pony", blush: "rgba(170,75,95,0.26)" },
  // Mixed / warm golden
  { skin: "#c9a07a", hair: "#4e342e", style: "side", blush: "rgba(220,110,130,0.36)" },
  { skin: "#deb887", hair: "#3e2723", style: "crop", blush: "rgba(230,115,135,0.38)" },
  { skin: "#bc8f6f", hair: "#212121", style: "bun", blush: "rgba(205,100,120,0.34)" },
];

/** How many distinct human looks the park can roll. */
export const HUMAN_LOOK_COUNT = HUMAN_LOOKS.length;

/** Park humans - sitting beggars / standing snack vendors / court players.
 * Sized so adults are just taller than the largest dog breeds (~1.85h).
 * Standing + seated park humans prefer Kenney Toon Characters (recolored);
 * chat / frisbee kids stay on hand-painted canvas. */
export function humanSprite(pose: HumanPose, variant = 0): SpriteSheet {
  const key = `${pose}:${variant}:${toonCharactersReady() ? "toon" : "canvas"}`;
  let sheet = humanCache.get(key);
  if (!sheet) {
    const toon = paintToonHuman(pose, variant);
    const canvas = toon ?? paintHuman(pose, variant);
    const fromToon = Boolean(toon);
    const athletic = pose.startsWith("tennis") || pose.startsWith("basket") || pose.startsWith("frisbee");
    const drumming = pose.startsWith("drum");
    const fishing = pose.startsWith("fish");
    const seated = pose === "sit" || pose === "eat";
    const kid = pose.startsWith("frisbee");
    // Biggest dogs are ~1.85 tall; adults land just above that.
    // Seated height is shorter so hips land on the bench with feet near the turf.
    const hs = 1.45;
    if (fromToon) {
      // Kenney frames are 96×128 — keep aspect, feet at texture bottom.
      const height =
        seated || drumming ? 1.55
        : fishing || athletic ? 2.1
        : 2.05;
      sheet = {
        texture: canvasTex(canvas),
        width: height * (96 / 128),
        height,
        seatAnchor: seated ? 0.28 : undefined,
        footY: 0.5,
      };
    } else {
      sheet = {
        texture: canvasTex(canvas),
        width: (seated || drumming ? 1.15 : fishing ? 1.2 : kid ? 0.78 : athletic ? 1.0 : 0.95) * hs,
        height: (seated ? 0.82 : drumming ? 1.35 : fishing ? 1.48 : kid ? 1.15 : athletic ? 1.5 : 1.45) * hs,
        // Fraction of sprite height from bottom → hips / seat contact.
        seatAnchor: pose === "sit" ? 0.46 : pose === "eat" ? 0.36 : undefined,
        // Seated planes use geometric center so seatAnchor math is exact.
        footY: seated ? 0.5 : undefined,
      };
    }
    humanCache.set(key, sheet);
  }
  return sheet;
}

/** Walking adult (Kenney walk cycle). Falls back to stand when toons aren't ready. */
export function humanWalkSprite(variant: number, step: number): SpriteSheet {
  if (!toonCharactersReady()) {
    return humanSprite("stand", variant);
  }
  const frame = ((step % 4) + 4) % 4;
  const key = `walk:${variant}:${frame}:toon`;
  let sheet = humanCache.get(key);
  if (!sheet) {
    const canvas = paintToonWalk(variant, frame);
    if (!canvas) {
      return humanSprite("stand", variant);
    }
    const height = 2.05;
    sheet = {
      texture: canvasTex(canvas),
      width: height * (96 / 128),
      height,
      footY: 0.5,
    };
    humanCache.set(key, sheet);
  }
  return sheet;
}

/** Benches and snack carts that characters sit on / stand beside. */
export function furnitureSprite(kind: FurnitureKind): SpriteSheet {
  let sheet = furnitureCache.get(kind);
  if (!sheet) {
    const canvas = paintFurniture(kind);
    if (kind === "bench") {
      // Match Kenney park-bench world size (local ~0.95 × WORLD_PROP_SCALE ≈ 1.45h).
      // seatY = fraction of canvas from bottom to seat plank × height.
      sheet = { texture: canvasTex(canvas), width: 1.85, height: 1.15, seatY: 1.15 * (26 / 56) };
    } else if (kind === "hotdogStand") {
      sheet = { texture: canvasTex(canvas), width: 1.7, height: 1.65 };
    } else if (kind === "iceCreamCart") {
      sheet = { texture: canvasTex(canvas), width: 1.45, height: 1.55 };
    } else if (kind === "pretzelCart") {
      sheet = { texture: canvasTex(canvas), width: 1.5, height: 1.35 };
    } else if (kind === "popcornCart") {
      sheet = { texture: canvasTex(canvas), width: 1.4, height: 1.55 };
    } else {
      sheet = { texture: canvasTex(canvas), width: 1.45, height: 1.7 };
    }
    furnitureCache.set(kind, sheet);
  }
  return sheet;
}

function canvasTex(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

function paintFurniture(kind: FurnitureKind): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = 80;
  c.height = kind === "bench" ? 56 : 80;
  const g = c.getContext("2d")!;
  g.imageSmoothingEnabled = false;

  if (kind === "bench") {
    // Side-view Holiday Kit orange park bench.
    const metal = "#b0bec5";
    const seat = "#e8a05a";
    const seatDark = "#d4894a";
    const seatLite = "#f0b878";
    // Legs / end frames
    fillRound(g, 8, 22, 7, 30, metal);
    fillRound(g, 65, 22, 7, 30, metal);
    // Seat planks
    fillRound(g, 6, 30, 68, 5, seat);
    fillRound(g, 6, 36, 68, 5, seatDark);
    // Backrest planks
    fillRound(g, 8, 8, 64, 5, seatDark);
    fillRound(g, 8, 15, 64, 5, seatLite);
    // Armrests
    fillRound(g, 8, 22, 8, 4, metal);
    fillRound(g, 64, 22, 8, 4, metal);
  } else if (kind === "hotdogStand") {
    fillRound(g, 8, 40, 64, 28, "#e53935");
    fillRound(g, 6, 36, 68, 8, "#ffc107");
    fillRound(g, 36, 18, 6, 20, "#b0bec5");
    // Umbrella
    g.fillStyle = "#ffeb3b";
    g.beginPath();
    g.moveTo(12, 22);
    g.lineTo(40, 8);
    g.lineTo(68, 22);
    g.closePath();
    g.fill();
    fillRound(g, 28, 30, 24, 8, "#ffcc80");
  } else if (kind === "iceCreamCart") {
    fillRound(g, 12, 38, 56, 30, "#29b6f6");
    fillRound(g, 10, 34, 60, 8, "#e1f5fe");
    fillRound(g, 48, 22, 10, 14, "#ffcc80");
    g.fillStyle = "#ff80ab";
    g.beginPath();
    g.moveTo(48, 22);
    g.lineTo(53, 8);
    g.lineTo(58, 22);
    g.closePath();
    g.fill();
  } else if (kind === "pretzelCart") {
    fillRound(g, 10, 40, 60, 26, "#6d4c41");
    fillRound(g, 14, 34, 52, 8, "#ffe082");
    for (const ox of [22, 40, 58]) {
      g.strokeStyle = "#d7ccc8";
      g.lineWidth = 4;
      g.beginPath();
      g.arc(ox, 30, 7, 0.2, Math.PI * 1.8);
      g.stroke();
    }
  } else if (kind === "popcornCart") {
    fillRound(g, 12, 42, 56, 26, "#fdd835");
    fillRound(g, 10, 48, 60, 8, "#e53935");
    fillRound(g, 18, 18, 44, 26, "rgba(179, 229, 252, 0.85)");
    for (let i = 0; i < 8; i++) {
      fillOval(g, 24 + (i % 4) * 8, 22 + Math.floor(i / 4) * 8, 6, 6, "#fffde7");
    }
  } else {
    // Balloon cart
    fillRound(g, 14, 48, 52, 22, "#8e24aa");
    const colors = ["#ef5350", "#42a5f5", "#ffee58", "#66bb6a", "#ff80ab"];
    for (let i = 0; i < 5; i++) {
      const bx = 18 + i * 10;
      const by = 10 + (i % 2) * 6;
      fillOval(g, bx, by, 12, 14, colors[i]!);
      g.strokeStyle = "#eeeeee";
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(bx + 6, by + 14);
      g.lineTo(40, 48);
      g.stroke();
    }
  }

  return paintPixelOutline(c, [255, 248, 231, 255], 1);
}

function paintCritter(kind: CritterKind): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = 80;
  c.height = 80;
  const g = c.getContext("2d")!;
  g.imageSmoothingEnabled = false;

  if (kind === "squirrel") {
    // Side-view park squirrel - bushy S-tail, warm brown, white belly
    fillOval(g, 4, 22, 28, 36, "#6d4c41");
    fillOval(g, 8, 18, 22, 28, "#8d6e63");
    fillOval(g, 12, 28, 14, 18, "#a1887f");
    // Body
    fillOval(g, 28, 34, 30, 24, "#a1887f");
    fillOval(g, 34, 40, 20, 14, "#efebe9");
    // Head
    fillOval(g, 48, 26, 22, 20, "#bcaaa4");
    fillOval(g, 52, 32, 14, 10, "#efebe9");
    // Ears + tufts
    fillOval(g, 52, 16, 8, 12, "#8d6e63");
    fillOval(g, 62, 18, 7, 10, "#8d6e63");
    fillOval(g, 54, 18, 4, 6, "#ef9a9a");
    fillOval(g, 63, 20, 3, 5, "#ef9a9a");
    // Tiny paws
    fillOval(g, 40, 50, 8, 6, "#8d6e63");
    fillOval(g, 52, 50, 8, 6, "#8d6e63");
    // Eye + nose
    g.fillStyle = "#3e2723";
    g.beginPath();
    g.arc(64, 34, 2.2, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#ffffff";
    g.beginPath();
    g.arc(64.8, 33.2, 0.9, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#5d4037";
    g.beginPath();
    g.arc(70, 36, 1.6, 0, Math.PI * 2);
    g.fill();
  } else if (kind === "rabbit") {
    // Soft cream bunny - upright ears, cotton puff
    fillOval(g, 8, 48, 14, 12, "#fff8e1"); // cotton tail
    fillOval(g, 18, 38, 34, 26, "#fff8e1");
    fillOval(g, 24, 46, 22, 14, "#fffde7");
    fillOval(g, 42, 32, 22, 20, "#fffde7");
    // Ears
    fillOval(g, 46, 4, 9, 30, "#fff8e1");
    fillOval(g, 58, 8, 8, 28, "#fff8e1");
    fillOval(g, 48, 10, 5, 18, "#f8bbd0");
    fillOval(g, 60, 14, 4, 16, "#f8bbd0");
    // Feet
    fillOval(g, 28, 56, 12, 8, "#ffe0b2");
    fillOval(g, 44, 56, 12, 8, "#ffe0b2");
    g.fillStyle = "#3e2723";
    g.beginPath();
    g.arc(56, 40, 2.2, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#ffffff";
    g.beginPath();
    g.arc(56.8, 39.2, 0.9, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#ec407a";
    g.beginPath();
    g.arc(64, 44, 1.8, 0, Math.PI * 2);
    g.fill();
    // Cheek
    g.fillStyle = "rgba(244, 143, 177, 0.7)";
    g.beginPath();
    g.ellipse(50, 46, 3.5, 2.2, 0, 0, Math.PI * 2);
    g.fill();
  } else if (kind === "pigeon") {
    // City pigeon - plump gray, soft neck sheen, coral feet
    fillOval(g, 14, 34, 40, 26, "#78909c");
    fillOval(g, 18, 40, 32, 16, "#90a4ae");
    fillOval(g, 20, 44, 24, 10, "#cfd8dc");
    // Wing stripe
    fillOval(g, 22, 38, 22, 10, "#607d8b");
    fillOval(g, 24, 40, 10, 4, "#eceff1");
    // Head + iridescent neck
    fillOval(g, 44, 28, 18, 16, "#90a4ae");
    fillOval(g, 46, 34, 12, 8, "#81c784");
    fillOval(g, 48, 32, 8, 6, "#7e57c2");
    // Beak
    g.fillStyle = "#ffb74d";
    g.beginPath();
    g.moveTo(60, 36);
    g.lineTo(72, 38);
    g.lineTo(60, 41);
    g.closePath();
    g.fill();
    // Feet
    g.strokeStyle = "#e57373";
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(28, 56);
    g.lineTo(26, 64);
    g.moveTo(32, 56);
    g.lineTo(34, 64);
    g.moveTo(42, 56);
    g.lineTo(40, 64);
    g.moveTo(46, 56);
    g.lineTo(48, 64);
    g.stroke();
    g.fillStyle = "#3e2723";
    g.beginPath();
    g.arc(56, 34, 2, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#ffffff";
    g.beginPath();
    g.arc(56.7, 33.3, 0.8, 0, Math.PI * 2);
    g.fill();
  } else if (kind === "duckling") {
    // Tiny yellow fluff - follows mom in a row
    fillOval(g, 22, 38, 28, 22, "#ffe082");
    fillOval(g, 26, 42, 20, 14, "#fff59d");
    fillOval(g, 42, 34, 18, 16, "#ffd54f");
    g.fillStyle = "#ff8a65";
    g.beginPath();
    g.moveTo(56, 40);
    g.lineTo(68, 42);
    g.lineTo(56, 46);
    g.closePath();
    g.fill();
    g.fillStyle = "#ff8a65";
    g.beginPath();
    g.ellipse(32, 60, 5, 2.5, -0.15, 0, Math.PI * 2);
    g.ellipse(42, 60, 5, 2.5, 0.15, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#3e2723";
    g.beginPath();
    g.arc(52, 40, 1.8, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#ffffff";
    g.beginPath();
    g.arc(52.6, 39.4, 0.7, 0, Math.PI * 2);
    g.fill();
  } else {
    // Cute mallard mom - yellow fluff body, orange beak/feet
    fillOval(g, 12, 36, 42, 26, "#ffd54f");
    fillOval(g, 18, 44, 30, 14, "#ffe082");
    // Wing
    fillOval(g, 20, 38, 22, 12, "#ffca28");
    // Head
    fillOval(g, 44, 26, 22, 20, "#66bb6a");
    fillOval(g, 48, 30, 14, 12, "#81c784");
    // Bill
    g.fillStyle = "#ff8a65";
    g.beginPath();
    g.moveTo(62, 36);
    g.lineTo(76, 38);
    g.lineTo(62, 44);
    g.closePath();
    g.fill();
    g.fillStyle = "#ffab91";
    g.beginPath();
    g.ellipse(68, 39, 4, 2.2, 0, 0, Math.PI * 2);
    g.fill();
    // Feet
    g.fillStyle = "#ff8a65";
    g.beginPath();
    g.ellipse(28, 62, 7, 3.5, -0.2, 0, Math.PI * 2);
    g.ellipse(44, 62, 7, 3.5, 0.2, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#3e2723";
    g.beginPath();
    g.arc(58, 34, 2.2, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#ffffff";
    g.beginPath();
    g.arc(58.8, 33.2, 0.9, 0, Math.PI * 2);
    g.fill();
  }

  return paintPixelOutline(c, [255, 248, 231, 255], 1);
}

function paintHuman(pose: HumanPose, variant: number): HTMLCanvasElement {
  const scale = 2;
  const pad = 3;
  const baseW = pose.startsWith("fish") ? 72 : 64;
  const baseH = pose === "sit" || pose === "eat" ? 96 : pose.startsWith("drum") ? 88 : pose.startsWith("fish") ? 84 : 80;
  const c = document.createElement("canvas");
  c.width = baseW * scale + pad * 2;
  c.height = baseH * scale + pad * 2;
  const g = c.getContext("2d")!;
  g.imageSmoothingEnabled = false;
  g.translate(pad, pad);
  g.scale(scale, scale);

  // Muted park-day clothes - earthy / cream, not neon Material pastels.
  const shirts = ["#8fb8c9", "#d4a090", "#7faf9e", "#d8c48a", "#b59bc4", "#8fad86", "#d2b08a", "#c99494", "#7fadb8", "#a8b88a"];
  const pants = ["#6d7d88", "#8a7468", "#7a8a94", "#9a8a7e", "#5a4038", "#4a5860"];
  const aprons = ["#fff6e4", "#f7efc8", "#e4f0f2", "#f6e6ec"];
  const shoes = ["#5d4037", "#3e464c", "#6d4c41", "#455a64", "#2a2420", "#3e2723"];
  const tennisShirts = ["#f7f4ec", "#e8f0f6", "#f5efe0", "#efe8f4", "#e8f2e8"];
  const tennisShorts = ["#3d6a9a", "#3f6e48", "#6a5080", "#4a5560", "#9a4848"];
  const basketJerseys = ["#c45a52", "#3d7ab0", "#4a8a58", "#d08840", "#7a5a98", "#3d8a7e"];
  const basketShorts = ["#2a2a2e", "#2f5a88", "#2e4e34", "#3a444c", "#4a3068"];
  const kidShirts = ["#d48868", "#5aa8c8", "#78a870", "#a878b0", "#d8b858", "#d07098"];
  const kidShorts = ["#4a8ab8", "#5a9a68", "#c86860", "#8a68a0", "#3e8e88"];
  const shirt = pose.startsWith("tennis")
    ? tennisShirts[variant % tennisShirts.length]!
    : pose.startsWith("basket")
      ? basketJerseys[variant % basketJerseys.length]!
      : pose.startsWith("frisbee")
        ? kidShirts[variant % kidShirts.length]!
        : pose === "cook"
          ? ["#d08040", "#d89850", "#d07858", "#d49070"][variant % 4]!
          : shirts[variant % shirts.length]!;
  const pant = pose.startsWith("tennis")
    ? tennisShorts[variant % tennisShorts.length]!
    : pose.startsWith("basket")
      ? basketShorts[variant % basketShorts.length]!
      : pose.startsWith("frisbee")
        ? kidShorts[variant % kidShorts.length]!
        : pants[variant % pants.length]!;
  const look = HUMAN_LOOKS[variant % HUMAN_LOOKS.length]!;
  const skin = look.skin;
  const apron = aprons[variant % aprons.length]!;
  const shoe = shoes[variant % shoes.length]!;
  const drumWood = ["#8d6d3a", "#a1887f", "#6d4c41", "#bfa06a"][variant % 4]!;
  const drumSkin = ["#ffe0b2", "#ffccbc", "#efebe9", "#fff8e1"][variant % 4]!;

  if (pose === "sit") {
    // Clean side-view sit: thighs on the seat, calves dangling straight down.
    fillRound(g, 16, 48, 24, 9, pant);
    fillRound(g, 34, 54, 8, 22, pant);
    fillOval(g, 32, 74, 12, 9, shoe);
    fillRound(g, 14, 46, 26, 10, pant);
    fillRound(g, 32, 52, 9, 24, pant);
    fillOval(g, 30, 74, 13, 10, shoe);
    fillOval(g, 32, 78, 8, 5, "#efebe9");
    fillRound(g, 14, 40, 26, 12, pant);
    fillRound(g, 16, 22, 24, 22, shirt);
    shadeStripe(g, 18, 24, 18, 8, "rgba(255,255,255,0.14)");
    fillRound(g, 8, 26, 10, 16, shirt);
    fillRound(g, 36, 28, 10, 14, shirt);
    fillOval(g, 40, 38, 10, 10, "#e8b878");
    fillOval(g, 8, 40, 8, 7, skin);
    fillOval(g, 38, 40, 8, 7, skin);
    fillOval(g, 13, 0, 34, 30, skin);
    paintHair(g, look, 13, -1, 34, 16);
    humanFace(g, 30, 14, look);
  } else if (pose === "eat") {
    fillRound(g, 10, 58, 22, 12, pant);
    fillRound(g, 34, 58, 22, 12, pant);
    fillOval(g, 8, 66, 14, 10, shoe);
    fillOval(g, 44, 66, 14, 10, shoe);
    fillRound(g, 16, 46, 32, 14, pant);
    fillRound(g, 18, 28, 28, 22, shirt);
    shadeStripe(g, 20, 30, 20, 8, "rgba(255,255,255,0.14)");
    fillOval(g, 22, 48, 22, 14, "#fff8e1");
    fillOval(g, 28, 50, 10, 8, "#d46a5a");
    fillRound(g, 10, 34, 10, 16, shirt);
    fillRound(g, 44, 32, 10, 18, shirt);
    fillOval(g, 10, 48, 8, 7, skin);
    fillOval(g, 46, 46, 8, 7, skin);
    fillOval(g, 15, 2, 34, 32, skin);
    paintHair(g, look, 15, 0, 34, 16);
    humanFace(g, 32, 17, look);
  } else if (pose === "cook") {
    fillRound(g, 22, 52, 20, 22, pant);
    fillOval(g, 20, 70, 12, 8, shoe);
    fillOval(g, 34, 70, 12, 8, shoe);
    fillRound(g, 20, 30, 24, 24, shirt);
    fillRound(g, 24, 34, 16, 20, apron);
    fillRound(g, 12, 34, 8, 16, shirt);
    fillRound(g, 42, 28, 8, 22, shirt);
    fillRound(g, 48, 18, 4, 18, "#78909c");
    fillRound(g, 44, 14, 12, 6, "#90a4ae");
    fillOval(g, 12, 48, 8, 7, skin);
    fillOval(g, 44, 48, 8, 7, skin);
    fillOval(g, 16, 4, 32, 30, skin);
    fillRound(g, 18, 0, 28, 12, "#fff8e1");
    humanFace(g, 32, 17, look);
  } else if (pose === "chat") {
    fillRound(g, 22, 52, 20, 22, pant);
    fillOval(g, 20, 70, 12, 8, shoe);
    fillOval(g, 34, 70, 12, 8, shoe);
    fillRound(g, 20, 30, 24, 24, shirt);
    shadeStripe(g, 22, 32, 16, 8, "rgba(255,255,255,0.14)");
    fillRound(g, 12, 34, 8, 16, shirt);
    fillRound(g, 42, 32, 8, 18, shirt);
    fillRound(g, 46, 48, 8, 14, "#7eb8d0");
    fillOval(g, 46, 44, 8, 6, "#fff6e0");
    fillOval(g, 12, 48, 8, 7, skin);
    fillOval(g, 44, 48, 8, 7, skin);
    fillOval(g, 16, 4, 32, 30, skin);
    paintHair(g, look, 16, 1, 32, 16);
    humanFace(g, 32, 17, look);
  } else if (pose === "drumReady" || pose === "drumHit") {
    fillRound(g, 10, 58, 22, 12, pant);
    fillRound(g, 34, 58, 22, 12, pant);
    fillOval(g, 8, 66, 14, 10, shoe);
    fillOval(g, 44, 66, 14, 10, shoe);
    const drumY = pose === "drumHit" ? 44 : 48;
    fillOval(g, 20, drumY + 8, 26, 22, drumWood);
    fillOval(g, 22, drumY, 22, 18, drumSkin);
    fillOval(g, 26, drumY + 4, 10, 6, "rgba(255,255,255,0.35)");
    const torsoY = pose === "drumHit" ? 26 : 30;
    fillRound(g, 20, torsoY, 24, 22, shirt);
    if (pose === "drumHit") {
      fillRound(g, 12, 28, 12, 18, shirt);
      fillRound(g, 40, 22, 12, 20, shirt);
      fillOval(g, 14, 42, 10, 8, skin);
      fillOval(g, 42, 36, 10, 8, skin);
    } else {
      fillRound(g, 12, 34, 10, 16, shirt);
      fillRound(g, 42, 34, 10, 16, shirt);
      fillOval(g, 12, 48, 9, 7, skin);
      fillOval(g, 44, 48, 9, 7, skin);
    }
    fillOval(g, 15, 2, 34, 32, skin);
    paintHair(g, look, 15, 0, 34, 16);
    humanFace(g, 32, 17, look);
  } else if (pose === "tennisReady" || pose === "tennisSwing") {
    fillRound(g, 22, 52, 20, 22, pant);
    fillOval(g, 20, 70, 12, 8, shoe);
    fillOval(g, 34, 70, 12, 8, shoe);
    fillRound(g, 20, 30, 24, 24, shirt);
    fillOval(g, 16, 4, 32, 30, skin);
    paintHair(g, look, 16, 1, 32, 15);
    humanFace(g, 32, 17, look);
    if (pose === "tennisSwing") {
      fillRound(g, 40, 18, 10, 22, shirt);
      fillRound(g, 46, 8, 4, 14, "#8d6e63");
      fillOval(g, 42, 0, 16, 16, "#a8c878");
      g.strokeStyle = "#5a7a40";
      g.lineWidth = 2;
      g.strokeRect(44, 2, 12, 12);
    } else {
      fillRound(g, 12, 34, 8, 16, shirt);
      fillRound(g, 42, 36, 8, 18, shirt);
      fillRound(g, 46, 48, 4, 12, "#8d6e63");
      fillOval(g, 42, 56, 14, 14, "#a8c878");
      g.strokeStyle = "#5a7a40";
      g.lineWidth = 2;
      g.strokeRect(44, 58, 10, 10);
    }
  } else if (pose === "basketReady" || pose === "basketShoot") {
    fillRound(g, 22, 52, 20, 22, pant);
    fillOval(g, 20, 70, 12, 8, shoe);
    fillOval(g, 34, 70, 12, 8, shoe);
    fillRound(g, 20, 30, 24, 24, shirt);
    shadeStripe(g, 24, 34, 14, 6, "rgba(255,255,255,0.18)");
    fillOval(g, 16, 4, 32, 30, skin);
    paintHair(g, look, 16, 1, 32, 15);
    humanFace(g, 32, 17, look);
    if (pose === "basketShoot") {
      fillRound(g, 12, 14, 10, 22, shirt);
      fillRound(g, 40, 10, 10, 24, shirt);
      fillOval(g, 12, 10, 9, 8, skin);
      fillOval(g, 42, 6, 9, 8, skin);
    } else {
      fillRound(g, 12, 34, 8, 16, shirt);
      fillRound(g, 42, 34, 8, 16, shirt);
      fillOval(g, 12, 48, 8, 7, skin);
      fillOval(g, 44, 48, 8, 7, skin);
    }
  } else if (pose === "frisbeeReady" || pose === "frisbeeThrow" || pose === "frisbeeCatch") {
    // Hands only - the real disc is a shared 3D mesh (same idea as basketball).
    fillRound(g, 24, 54, 16, 18, pant);
    fillOval(g, 22, 70, 10, 7, shoe);
    fillOval(g, 34, 70, 10, 7, shoe);
    fillRound(g, 22, 34, 20, 22, shirt);
    fillOval(g, 18, 8, 28, 28, skin);
    paintHair(g, look, 18, 6, 28, 14);
    humanFace(g, 32, 21, look);
    if (pose === "frisbeeThrow") {
      fillRound(g, 8, 36, 8, 16, shirt);
      fillOval(g, 6, 48, 8, 7, skin);
      fillRound(g, 38, 24, 12, 18, shirt);
      fillOval(g, 44, 22, 7, 6, skin);
    } else if (pose === "frisbeeCatch") {
      fillRound(g, 10, 18, 9, 18, shirt);
      fillRound(g, 43, 16, 9, 20, shirt);
      fillOval(g, 8, 14, 9, 8, skin);
      fillOval(g, 44, 14, 8, 7, skin);
    } else {
      fillRound(g, 12, 38, 8, 14, shirt);
      fillRound(g, 42, 36, 9, 16, shirt);
      fillOval(g, 12, 48, 8, 7, skin);
      fillOval(g, 40, 48, 7, 6, skin);
    }
  } else if (pose === "fishReady" || pose === "fishCast") {
    // Standing pier angler - rod held out toward local right (water).
    const rod = "#6d4c41";
    const reel = "#78909c";
    fillRound(g, 22, 52, 20, 22, pant);
    fillOval(g, 20, 70, 12, 8, shoe);
    fillOval(g, 34, 70, 12, 8, shoe);
    fillRound(g, 20, 30, 24, 24, shirt);
    shadeStripe(g, 22, 32, 16, 8, "rgba(255,255,255,0.14)");
    fillOval(g, 16, 4, 32, 30, skin);
    paintHair(g, look, 16, 1, 32, 15);
    humanFace(g, 32, 17, look);
    if (pose === "fishCast") {
      fillRound(g, 12, 28, 8, 16, shirt);
      fillOval(g, 12, 42, 8, 7, skin);
      fillRound(g, 40, 14, 10, 22, shirt);
      fillOval(g, 44, 12, 8, 7, skin);
      fillRound(g, 48, 2, 4, 28, rod);
      fillOval(g, 46, 24, 8, 6, reel);
      g.strokeStyle = "rgba(144,164,174,0.7)";
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(50, 2);
      g.lineTo(66, 18);
      g.stroke();
    } else {
      fillRound(g, 12, 34, 8, 16, shirt);
      fillOval(g, 12, 48, 8, 7, skin);
      fillRound(g, 40, 32, 10, 18, shirt);
      fillOval(g, 46, 40, 8, 7, skin);
      fillRound(g, 48, 28, 4, 22, rod);
      fillOval(g, 46, 42, 8, 6, reel);
      fillRound(g, 50, 18, 3, 14, rod);
      g.strokeStyle = "rgba(144,164,174,0.65)";
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(52, 18);
      g.lineTo(68, 36);
      g.stroke();
    }
  } else {
    // Standing vendor / performer - apron for snack sellers, diverse looks.
    fillRound(g, 22, 52, 20, 22, pant);
    fillOval(g, 20, 70, 12, 8, shoe);
    fillOval(g, 34, 70, 12, 8, shoe);
    fillRound(g, 20, 32, 24, 22, shirt);
    shadeStripe(g, 22, 34, 16, 8, "rgba(255,255,255,0.14)");
    fillRound(g, 24, 36, 16, 18, apron);
    fillRound(g, 12, 34, 8, 16, shirt);
    fillRound(g, 44, 34, 8, 16, shirt);
    fillOval(g, 16, 6, 32, 30, skin);
    paintHair(g, look, 16, 2, 32, 16);
    if (variant % 5 === 0) {
      fillOval(g, 44, 2, 12, 12, "#e8a0c0");
      fillOval(g, 46, 4, 6, 5, "rgba(255,255,255,0.35)");
    }
    humanFace(g, 32, 19, look);
  }

  g.setTransform(1, 0, 0, 1, 0, 0);
  return cutifyHumanFrame(c);
}

/** Soft chibi polish for park people - same family as dog cutify. */
function cutifyHumanFrame(source: HTMLCanvasElement): HTMLCanvasElement {
  const g = source.getContext("2d")!;
  g.imageSmoothingEnabled = false;
  g.globalCompositeOperation = "source-atop";

  g.fillStyle = "rgba(255, 220, 200, 0.14)";
  g.fillRect(0, 0, source.width, source.height);
  g.fillStyle = "rgba(255, 170, 190, 0.07)";
  g.fillRect(0, 0, source.width, source.height);

  // Soft cheek wash across the upper torso / face band.
  const cheekY = source.height * 0.28;
  const cheekR = Math.max(5, source.width * 0.09);
  g.fillStyle = "rgba(255, 130, 160, 0.22)";
  g.beginPath();
  g.ellipse(source.width * 0.38, cheekY, cheekR, cheekR * 0.7, 0, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.ellipse(source.width * 0.62, cheekY, cheekR * 0.95, cheekR * 0.65, 0, 0, Math.PI * 2);
  g.fill();

  g.fillStyle = "rgba(255, 255, 255, 0.55)";
  g.beginPath();
  g.ellipse(source.width * 0.42, source.height * 0.12, source.width * 0.08, source.height * 0.035, -0.3, 0, Math.PI * 2);
  g.fill();

  g.globalCompositeOperation = "source-over";
  return paintPixelOutline(source, [255, 248, 231, 255], 1);
}

function shadeStripe(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  fillRound(g, x, y, w, h, color);
}

/** Hair / head covering for a look, drawn over the head oval. */
function paintHair(
  g: CanvasRenderingContext2D,
  look: HumanLook,
  hx: number,
  hy: number,
  hw: number,
  hh: number,
): void {
  const { hair, style } = look;
  const cx = hx + hw / 2;
  switch (style) {
    case "bald":
      // Soft shine only - no hair mass.
      fillOval(g, cx - 4, hy + 2, 8, 4, "rgba(255,255,255,0.12)");
      break;
    case "crop":
      fillOval(g, hx + 2, hy, hw - 4, hh * 0.7, hair);
      break;
    case "short":
      fillOval(g, hx + 1, hy - 1, hw - 2, hh * 0.85, hair);
      break;
    case "side":
      fillOval(g, hx + 2, hy, hw - 4, hh * 0.75, hair);
      fillOval(g, hx + hw * 0.55, hy + hh * 0.35, hw * 0.42, hh * 0.85, hair);
      break;
    case "long":
      fillOval(g, hx, hy - 1, hw, hh * 0.8, hair);
      fillOval(g, hx - 1, hy + hh * 0.4, hw * 0.35, hh * 1.1, hair);
      fillOval(g, hx + hw * 0.65, hy + hh * 0.4, hw * 0.38, hh * 1.1, hair);
      break;
    case "pony":
      fillOval(g, hx + 2, hy, hw - 4, hh * 0.75, hair);
      fillOval(g, hx + hw * 0.55, hy + hh * 0.15, 12, 18, hair);
      fillOval(g, hx + hw * 0.62, hy + hh * 0.85, 10, 14, hair);
      break;
    case "bun":
      fillOval(g, hx + 3, hy + 2, hw - 6, hh * 0.65, hair);
      fillOval(g, cx - 8, hy - 6, 16, 14, hair);
      break;
    case "puff":
      fillOval(g, hx - 2, hy - 4, hw + 4, hh + 4, hair);
      fillOval(g, hx + 4, hy - 2, hw - 8, hh * 0.55, hair);
      break;
    case "braids":
      fillOval(g, hx + 2, hy, hw - 4, hh * 0.7, hair);
      fillOval(g, hx - 2, hy + hh * 0.45, 10, 22, hair);
      fillOval(g, hx + hw - 8, hy + hh * 0.45, 10, 22, hair);
      fillOval(g, hx, hy + hh * 1.15, 8, 8, hair);
      fillOval(g, hx + hw - 8, hy + hh * 1.15, 8, 8, hair);
      break;
    case "scarf":
      // Soft wrap / headscarf silhouette.
      fillOval(g, hx - 2, hy - 2, hw + 4, hh + 6, hair);
      fillOval(g, hx + 2, hy + hh * 0.55, hw - 4, hh * 0.7, hair);
      fillOval(g, hx + hw * 0.15, hy + 2, hw * 0.7, hh * 0.45, "rgba(255,255,255,0.12)");
      break;
    default:
      fillOval(g, hx + 2, hy, hw - 4, hh * 0.8, hair);
  }
}

function face(g: CanvasRenderingContext2D, cx: number, cy: number, s: number): void {
  g.fillStyle = "#3e2723";
  g.beginPath();
  g.arc(cx - 3 * s, cy, 1.6 * s, 0, Math.PI * 2);
  g.arc(cx + 3 * s, cy, 1.6 * s, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = "rgba(244, 143, 177, 0.75)";
  g.beginPath();
  g.ellipse(cx - 5 * s, cy + 3 * s, 2.2 * s, 1.4 * s, 0, 0, Math.PI * 2);
  g.ellipse(cx + 5 * s, cy + 3 * s, 2.2 * s, 1.4 * s, 0, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = "rgba(255,255,255,0.9)";
  g.beginPath();
  g.arc(cx + 2 * s, cy - 3 * s, 1.2 * s, 0, Math.PI * 2);
  g.fill();
}

function humanFace(g: CanvasRenderingContext2D, cx: number, cy: number, look: HumanLook): void {
  // Soft brow shadow so the face reads against skin.
  g.fillStyle = "rgba(60, 40, 30, 0.08)";
  g.beginPath();
  g.ellipse(cx, cy - 1, 11, 8, 0, 0, Math.PI * 2);
  g.fill();

  // Big chibi eyes - white sclera + dark pupil + sparkle (dog-adjacent cute).
  for (const side of [-1, 1] as const) {
    const ex = cx + side * 5.5;
    g.fillStyle = "#fff8f0";
    g.beginPath();
    g.ellipse(ex, cy, 3.6, 4.2, 0, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#2a1810";
    g.beginPath();
    g.ellipse(ex + side * 0.2, cy + 0.4, 2.2, 2.6, 0, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "rgba(255,255,255,0.95)";
    g.beginPath();
    g.arc(ex - 0.8, cy - 1.2, 1.15, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.arc(ex + 0.9, cy + 0.6, 0.55, 0, Math.PI * 2);
    g.fill();
  }

  g.fillStyle = look.blush;
  g.beginPath();
  g.ellipse(cx - 10, cy + 5.5, 4.2, 2.6, 0, 0, Math.PI * 2);
  g.ellipse(cx + 10, cy + 5.5, 4.2, 2.6, 0, 0, Math.PI * 2);
  g.fill();

  // Tiny soft smile.
  g.strokeStyle = "rgba(90, 50, 40, 0.45)";
  g.lineWidth = 1.4;
  g.lineCap = "round";
  g.beginPath();
  g.arc(cx, cy + 7.5, 3.2, 0.15, Math.PI - 0.15);
  g.stroke();
}

function fillOval(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  g.fillStyle = color;
  g.beginPath();
  g.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  g.fill();
}

function fillRound(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  const r = Math.min(4, w / 3, h / 3);
  g.fillStyle = color;
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
  g.fill();
}

function paintPixelOutline(
  source: HTMLCanvasElement,
  rgb: [number, number, number, number],
  radius: number,
): HTMLCanvasElement {
  const w = source.width;
  const h = source.height;
  const ctx = source.getContext("2d")!;
  const src = ctx.getImageData(0, 0, w, h).data;
  const out = ctx.createImageData(w, h);
  const dst = out.data;
  const solid = (x: number, y: number): boolean => {
    if (x < 0 || y < 0 || x >= w || y >= h) {
      return false;
    }
    return src[(y * w + x) * 4 + 3]! > 40;
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (src[i + 3]! > 40) {
        dst[i] = src[i]!;
        dst[i + 1] = src[i + 1]!;
        dst[i + 2] = src[i + 2]!;
        dst[i + 3] = src[i + 3]!;
        continue;
      }
      let ring = false;
      for (let oy = -radius; oy <= radius && !ring; oy++) {
        for (let ox = -radius; ox <= radius; ox++) {
          if (ox * ox + oy * oy > radius * radius + radius) {
            continue;
          }
          if (solid(x + ox, y + oy)) {
            ring = true;
            break;
          }
        }
      }
      if (ring) {
        dst[i] = rgb[0];
        dst[i + 1] = rgb[1];
        dst[i + 2] = rgb[2];
        dst[i + 3] = rgb[3];
      }
    }
  }
  ctx.putImageData(out, 0, 0);
  return source;
}
